import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { supabase } from '../lib/supabase'
import { useAuthContext } from './AuthContext'

interface Organization {
  organization_id: string
  name: string
  slug: string
  contact_email: string | null
  contact_phone: string | null
  config: Record<string, unknown>
}

interface UserProfile {
  id: string
  organization_id: string
  email: string
  name: string | null
  role: 'org_admin' | 'staff'
  tier: 'free' | 'pro' | 'enterprise'
  is_platform_admin: boolean
}

interface TenantContextType {
  organization: Organization | null
  profile: UserProfile | null
  isLoading: boolean
  error: string | null
  refetch: () => Promise<void>
}

const TenantContext = createContext<TenantContextType | undefined>(undefined)

export function TenantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuthContext()
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchTenantData = async () => {
    if (!user) {
      setOrganization(null)
      setProfile(null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (profileError) throw profileError

      setProfile(profileData)

      // Fetch organization
      if (profileData.organization_id) {
        const { data: orgData, error: orgError } = await supabase
          .from('organizations')
          .select('*')
          .eq('organization_id', profileData.organization_id)
          .single()

        if (orgError) throw orgError
        setOrganization(orgData)
      }
    } catch (err) {
      console.error('Error fetching tenant data:', err)
      setError(err instanceof Error ? err.message : 'Failed to load tenant data')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchTenantData()
  }, [user])

  return (
    <TenantContext.Provider value={{ 
      organization, 
      profile, 
      isLoading, 
      error,
      refetch: fetchTenantData 
    }}>
      {children}
    </TenantContext.Provider>
  )
}

export function useTenantContext() {
  const context = useContext(TenantContext)
  if (context === undefined) {
    throw new Error('useTenantContext must be used within a TenantProvider')
  }
  return context
}
